import { useState } from "react";
import { useI18n } from "../../../../packages/excalidraw/i18n";
import { useAuth } from "../../../hooks/useAuth";
import { isLegacyFeedbackModalOpen } from "../FeedbackModal";
import FeedbackNudge from "./FeedbackNudge";
import { resetPendingTrigger, useFeedbackNudge } from "./feedbackNudgeLogic";
import {
  FeedbackDuplicateError,
  FeedbackValues,
  newSubmissionId,
  submitFeedbackResponse,
} from "../../../services/feedbackSubmission";
import FeedbackDialog from "./FeedbackDialog";
import { ACTIVE_FEEDBACK_FORM } from "./forms";
import { useFeedbackButtonVisible } from "./useFeedbackButtonVisible";

/**
 * The footer entry point for the active feedback form.
 *
 * Rendered into the bottom-right slot beside the help button on desktop, and
 * into the misc tool column on mobile. Anyone may submit, signed in or not;
 * only signed-in users get their submission recorded against them and the
 * button hidden for good.
 */
const FeedbackButton = () => {
  const { t } = useI18n();
  const { session, feedbackSubmitted } = useAuth();
  const { visible, markSubmitted } =
    useFeedbackButtonVisible(ACTIVE_FEEDBACK_FORM);
  const [isOpen, setIsOpen] = useState(false);

  // The nudge asks the same question the button does, so it is eligible on the
  // same terms: `visible` already accounts for cooldown and prior submission.
  // The legacy onboarding modal covers the whole app and cannot be escaped, so
  // nothing may sit over it.
  const nudge = useFeedbackNudge({
    userId: session?.user?.id ?? null,
    eligible:
      visible &&
      !isOpen &&
      !isLegacyFeedbackModalOpen({ session, feedbackSubmitted }),
    formKey: ACTIVE_FEEDBACK_FORM.key,
  });

  // The row's primary key, minted once per opening and reused by every retry
  // within it. A retry after a lost response then collides with the row it
  // already wrote instead of writing a second one — see submitFeedbackResponse.
  // A fresh opening is a fresh answer and gets a fresh id.
  const [submissionId, setSubmissionId] = useState(newSubmissionId);

  const openDialog = () => {
    // Drop any trigger still waiting: closing this dialog flips `eligible`
    // back to true, which would otherwise resurface the bubble straight away.
    resetPendingTrigger();
    setSubmissionId(newSubmissionId());
    setIsOpen(true);
  };

  const handleSubmit = async (values: FeedbackValues) => {
    try {
      await submitFeedbackResponse(
        ACTIVE_FEEDBACK_FORM,
        values,
        session?.user?.id ?? null,
        submissionId,
      );
    } catch (error) {
      // A duplicate is the server confirming a response is already stored, so
      // stop asking even though this attempt failed. Without this the button
      // stays up and every retry hits the same rejection until a reload.
      if (error instanceof FeedbackDuplicateError) {
        markSubmitted();
      }
      // Rethrown either way: FeedbackDialog catches it and shows the error
      // panel, so the user is told their answers were not stored.
      throw error;
    }

    // Recorded now, not when the dialog is dismissed: the submission is already
    // durable server-side, and a user who reloads instead of clicking Done
    // should not be asked again.
    markSubmitted();
  };

  return (
    <>
      {visible && (
        // Positioned, so the coach-mark can tether to this button.
        <div className="feedback-nudge-anchor">
          <button
            className="feedback-footer-btn"
            onClick={openDialog}
            type="button"
            title={t("mindmap.feedbackButton")}
            aria-label={t("mindmap.feedbackButton")}
            data-testid="feedback-button"
          >
            {t("mindmap.feedbackButton")}
          </button>

          <FeedbackNudge
            visible={nudge.visible}
            onAccept={() => {
              nudge.onAccept();
              openDialog();
            }}
            onDismiss={nudge.onDismiss}
          />
        </div>
      )}

      {/*
        Rendered unconditionally rather than behind `visible`. `markSubmitted()`
        flips `visible` to false the moment a submission lands, so any condition
        involving it takes this element away mid-transition: gating on `visible`
        alone kills the confirmation panel outright, and `visible || isOpen`
        merely moves the tear-down to the Done click — both `visible` and
        `isOpen` go false in the same commit, the element unmounts, and the
        dialog vanishes instead of fading. Closed, the Transition renders
        nothing, so leaving it mounted costs a component and buys the leave
        animation in every case.
      */}
      <FeedbackDialog
        isOpen={isOpen}
        form={ACTIVE_FEEDBACK_FORM}
        onClose={() => setIsOpen(false)}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default FeedbackButton;
